import React, { useState, useMemo } from "react";
import { Globe, Check, Search, ChevronDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/hooks/useLanguage";
import { type Language } from "@/lib/languages";
import { cn } from "@/lib/utils";

interface LanguageSelectorProps {
  variant?: "header" | "mobile" | "footer" | "compact";
  className?: string;
}

export function LanguageSelector({ variant = "header", className }: LanguageSelectorProps) {
  const { currentLanguage, currentLanguageObj, setLanguage, isLoading, supportedLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return supportedLanguages;
    const q = searchQuery.toLowerCase().trim();
    return supportedLanguages.filter(
      (lang) =>
        lang.name.toLowerCase().includes(q) ||
        lang.nativeName.toLowerCase().includes(q) ||
        lang.code.toLowerCase().includes(q)
    );
  }, [searchQuery, supportedLanguages]);

  const handleSelect = (code: string) => {
    setLanguage(code);
    setOpen(false);
    setSearchQuery("");
  };

  // ── Variant: Footer ────────────────────────────────────────────────
  if (variant === "footer") {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors border border-border/40",
              className
            )}
            aria-label="Change language"
          >
            <Globe className="h-3.5 w-3.5 text-primary" />
            <span className="flex items-center gap-1.5">
              <span>{currentLanguageObj.flag}</span>
              <span>{currentLanguageObj.nativeName}</span>
            </span>
            <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="top"
          className="w-72 p-2 rounded-2xl bg-popover/95 backdrop-blur-xl border border-border/60 shadow-2xl z-50 notranslate"
          translate="no"
        >
          <LanguageList
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredLanguages={filteredLanguages}
            currentLanguage={currentLanguage}
            onSelect={handleSelect}
            isLoading={isLoading}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // ── Variant: Mobile ────────────────────────────────────────────────
  if (variant === "mobile") {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "w-full flex items-center justify-between px-4 py-3 rounded-xl bg-accent/40 hover:bg-accent/70 text-sm font-semibold text-foreground transition-all border border-border/40",
              className
            )}
            aria-label="Change website language"
          >
            <div className="flex items-center gap-2.5">
              <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
                <Globe className="h-4 w-4" />
              </span>
              <div className="text-left">
                <div className="text-xs text-muted-foreground font-medium">Language</div>
                <div className="text-sm font-bold flex items-center gap-1.5">
                  <span>{currentLanguageObj.flag}</span>
                  <span>{currentLanguageObj.nativeName}</span>
                  <span className="text-xs text-muted-foreground font-normal">({currentLanguageObj.name})</span>
                </div>
              </div>
            </div>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          className="w-[calc(100vw-2.5rem)] max-w-sm p-2 rounded-2xl bg-popover/95 backdrop-blur-xl border border-border/60 shadow-2xl z-50 notranslate"
          translate="no"
        >
          <LanguageList
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredLanguages={filteredLanguages}
            currentLanguage={currentLanguage}
            onSelect={handleSelect}
            isLoading={isLoading}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // ── Variant: Header & Compact ──────────────────────────────────────
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-10 px-2.5 sm:px-3 flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 hover:bg-accent hover:border-primary/40 text-xs font-semibold text-foreground transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary shadow-sm active:scale-95",
            className
          )}
          aria-label="Change language"
          title={`Language: ${currentLanguageObj.name}`}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
          ) : (
            <Globe className="h-4 w-4 text-primary shrink-0" />
          )}
          <span className="hidden sm:inline-flex items-center gap-1">
            <span>{currentLanguageObj.flag}</span>
            <span className="uppercase text-[11px] font-bold tracking-wider">{currentLanguageObj.code.split("-")[0]}</span>
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="w-72 p-2 rounded-2xl bg-popover/95 backdrop-blur-xl border border-border/60 shadow-2xl z-50 notranslate"
        translate="no"
      >
        <LanguageList
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredLanguages={filteredLanguages}
          currentLanguage={currentLanguage}
          onSelect={handleSelect}
          isLoading={isLoading}
        />
      </PopoverContent>
    </Popover>
  );
}

interface LanguageListProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredLanguages: Language[];
  currentLanguage: string;
  onSelect: (code: string) => void;
  isLoading: boolean;
}

function LanguageList({
  searchQuery,
  setSearchQuery,
  filteredLanguages,
  currentLanguage,
  onSelect,
  isLoading,
}: LanguageListProps) {
  return (
    <div className="space-y-2">
      {/* Header & Search */}
      <div className="px-2 pt-1 pb-1">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 text-primary" />
            <span>Select Language</span>
          </div>
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search language..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-8 pr-3 rounded-lg bg-accent/50 border border-transparent focus:border-primary/40 focus:bg-background text-xs transition-colors focus:outline-none"
            autoFocus
          />
        </div>
      </div>

      {/* Language Scroll Area */}
      <div className="max-h-60 overflow-y-auto space-y-0.5 pr-1 scrollbar-thin">
        {filteredLanguages.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            No language found matching "{searchQuery}"
          </div>
        ) : (
          filteredLanguages.map((lang) => {
            const isSelected = currentLanguage === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => onSelect(lang.code)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-all text-left group",
                  isSelected
                    ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                    : "text-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-base leading-none shrink-0">{lang.flag}</span>
                  <div className="min-w-0 truncate">
                    <span className="block truncate font-semibold leading-tight">{lang.nativeName}</span>
                    <span
                      className={cn(
                        "block text-[10px] truncate mt-0.5",
                        isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                      )}
                    >
                      {lang.name}
                    </span>
                  </div>
                </div>
                {isSelected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
